import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import {
  createLocationType,
  getPlatformSectionVisibility,
  useQuery,
} from "wasp/client/operations";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { ArrowLeft } from "lucide-react";
import DefaultLayout from "../../admin/layout/DefaultLayout";
import { toast } from "../../hooks/use-toast";
import { SECTION_LABELS, SIDEBAR_SECTION_KEYS } from "./constants";

const CreateLocationTypePage = () => {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: platformVisibility } = useQuery(getPlatformSectionVisibility);
  const platformByKey =
    (platformVisibility as { byKey?: Record<string, boolean> } | undefined)
      ?.byKey ?? {};
  const visibleSectionKeys = SIDEBAR_SECTION_KEYS.filter(
    (k) => platformByKey[k] !== false,
  );

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    iconName: "MapPin",
    color: "#64748B",
    // Section visibility - all enabled by default
    showNotice: true,
    showEvent: true,
    showFacility: true,
    showTenantBusiness: true,
    showMarketplaceItem: true,
    showService: true,
    showLostItem: true,
    showFoundItem: true,
    showAnnouncement: true,
    showBuySell: true,
    showFoodCourt: true,
    showRequestOffer: true,
    showRetail: true,
    showUrgentHelp: true,
    showAskForHelp: true,
    showShareWithNeighbors: true,
    showLostFound: true,
    showOtherRequests: true,
    showJob: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Location type name is required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await createLocationType(formData);

      toast({
        title: "Success",
        description: "Location type created successfully",
      });

      navigate("/admin/location-types");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create location type",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user?.isAdmin) {
    return <div>Access denied. Admin privileges required.</div>;
  }

  return (
    <DefaultLayout user={user}>
      <div className="w-full max-w-4xl mx-auto py-8 px-4">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/location-types")}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Location Types
          </Button>

          <h1 className="text-3xl font-bold text-gray-900">
            Create Location Type
          </h1>
          <p className="text-sm text-gray-600 mt-2">
            Define a new location type with default section visibility settings
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-6 bg-white rounded-lg border p-6"
        >
          {/* Basic Information */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b pb-2">
              Basic Information
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Innovation Hub, Shopping Center"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Brief description of this location type"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) =>
                      setFormData({ ...formData, color: e.target.value })
                    }
                    className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) =>
                      setFormData({ ...formData, color: e.target.value })
                    }
                    placeholder="#64748B"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section Visibility */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 border-b pb-2">
              Default Section Visibility
            </h2>
            <p className="text-sm text-gray-600">
              Only sections that are visible in Platform section visibility are
              listed. Configure defaults for locations of this type; individual
              locations can override these.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visibleSectionKeys.length === 0 ? (
                <p className="text-sm text-gray-500 col-span-full">
                  No sections are visible globally. Enable sections in Platform
                  section visibility first.
                </p>
              ) : (
                visibleSectionKeys.map((key) => (
                  <div
                    key={key}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded"
                  >
                    <span className="text-sm font-medium text-gray-700">
                      {SECTION_LABELS[key] ?? key}
                    </span>
                    <Switch
                      checked={
                        formData[key as keyof typeof formData] as boolean
                      }
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, [key]: checked })
                      }
                    />
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/location-types")}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Location Type"}
            </Button>
          </div>
        </form>
      </div>
    </DefaultLayout>
  );
};

export default CreateLocationTypePage;
